// Phase 0093-B1 — Study Directions: My Directed Studies (Today).
// Plus admin section for editing direction rules + generation logs.

import { useState } from 'react'
import {
  Alert, App, Button, Card, Col, Empty, Modal, Popconfirm, Row, Skeleton,
  Space, Table, Tabs, Tag, Tooltip, Typography,
} from 'antd'
import {
  PlayCircleOutlined, ReloadOutlined, RollbackOutlined, ToolOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import {
  studyDirectionApi, type DirectionRule, type DirectionToday, type GenerationLog,
} from '@/features/hr/studyDirectionApi'

const { Text, Title } = Typography

function PriorityTag({ p }: { p: string | null }) {
  if (!p) return null
  const color = p === 'P1' ? 'red' : p === 'P2' ? 'orange' : 'blue'
  return <Tag color={color}>{p}</Tag>
}

function MyDirectedStudies() {
  const navigate = useNavigate()
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['study-directions-today', 'me'],
    queryFn: () => studyDirectionApi.today(true),
  })

  const startStudy = (row: DirectionToday) => {
    if (!row.study_template_id) return
    // Hand off to the live-study capture router with target context as
    // query params; the capture screens read them on mount.
    const params = new URLSearchParams()
    if (row.target_department_id) params.set('department_id', row.target_department_id)
    if (row.target_employee_id) params.set('employee_id', row.target_employee_id)
    if (row.target_machine_id) params.set('machine_id', row.target_machine_id)
    if (row.id) params.set('linked_task_id', row.id)
    navigate(`/hr/live-studies/start?template_id=${row.study_template_id}&${params.toString()}`)
  }

  const columns = [
    { title: 'Study', dataIndex: 'title', key: 'title',
      render: (t: string) => <Text strong>{t}</Text> },
    { title: 'Department', dataIndex: 'target_department', key: 'target_department',
      render: (v: string | null) => v ?? '—' },
    { title: 'Employee', dataIndex: 'target_employee', key: 'target_employee',
      render: (v: string | null) => v ?? '—' },
    { title: 'Reason', dataIndex: 'target_reason', key: 'target_reason',
      render: (v: string | null) => v ?? '—' },
    { title: 'Priority', dataIndex: 'target_priority', key: 'target_priority',
      render: (p: string | null) => <PriorityTag p={p} /> },
    { title: 'Due', dataIndex: 'due_at', key: 'due_at',
      render: (d: string | null) => d ? new Date(d).toLocaleString() : '—' },
    { title: 'Status', dataIndex: 'status', key: 'status',
      render: (s: string) => <Tag>{s}</Tag> },
    { title: '', key: 'action',
      render: (_: unknown, r: DirectionToday) => (
        <Button
          size="small" type="primary"
          icon={<PlayCircleOutlined />}
          disabled={!r.study_template_id}
          onClick={() => startStudy(r)}
        >
          Start
        </Button>
      ) },
  ]

  if (isLoading) return <Skeleton active />
  if (!data?.length) return <Empty description="No directed studies for today." />

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} size="small"
                onClick={() => refetch()} loading={isFetching}>
          Refresh
        </Button>
      </Space>
      <Table
        rowKey="id"
        columns={columns as never}
        dataSource={data}
        pagination={{ pageSize: 20 }}
        size="small"
      />
    </>
  )
}

function DirectionRulesAdmin() {
  const qc = useQueryClient()
  const { message } = App.useApp()
  const [selected, setSelected] = useState<DirectionRule | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['study-direction-rules'],
    queryFn: () => studyDirectionApi.rules(),
  })

  const deactMut = useMutation({
    mutationFn: (id: string) => studyDirectionApi.deactivate(id),
    onSuccess: () => {
      message.success('Rule deactivated.')
      qc.invalidateQueries({ queryKey: ['study-direction-rules'] })
    },
  })
  const resetMut = useMutation({
    mutationFn: (id: string) => studyDirectionApi.resetToFactory(id),
    onSuccess: () => {
      message.success('Rule reset to factory.')
      qc.invalidateQueries({ queryKey: ['study-direction-rules'] })
    },
  })
  const runMut = useMutation({
    mutationFn: (id: string) => studyDirectionApi.runNow(id),
    onSuccess: (logrow) => {
      message.success(
        `Ran ${logrow.rule_code}: ${logrow.tasks_created} created, ` +
        `${logrow.tasks_skipped_duplicate} skipped.`,
      )
      qc.invalidateQueries({ queryKey: ['study-directions-today', 'me'] })
    },
  })
  const reseedMut = useMutation({
    mutationFn: () => studyDirectionApi.reseed(),
    onSuccess: () => {
      message.success('Factory rules reseeded.')
      qc.invalidateQueries({ queryKey: ['study-direction-rules'] })
    },
  })
  const runAllMut = useMutation({
    mutationFn: () => studyDirectionApi.runAll(),
    onSuccess: (rows) => {
      const total = rows.reduce((acc, r) => acc + r.tasks_created, 0)
      message.success(`Generated ${total} directed tasks across ${rows.length} rules.`)
      qc.invalidateQueries({ queryKey: ['study-directions-today', 'me'] })
    },
  })

  if (isLoading) return <Skeleton active />

  const columns = [
    { title: 'Code', dataIndex: 'rule_code', key: 'rule_code',
      render: (c: string) => <code style={{ fontSize: 12 }}>{c}</code> },
    { title: 'Name', dataIndex: 'rule_name', key: 'rule_name' },
    { title: 'Study', dataIndex: 'study_code', key: 'study_code',
      render: (s: string) => <Tag color="blue">{s}</Tag> },
    { title: 'Trigger', dataIndex: 'trigger_type', key: 'trigger_type' },
    { title: 'Freq', dataIndex: 'frequency', key: 'frequency' },
    { title: 'Owner role', dataIndex: 'owner_role_code', key: 'owner_role_code' },
    { title: 'Due', dataIndex: 'due_time_local', key: 'due_time_local' },
    { title: 'Active', dataIndex: 'active', key: 'active',
      render: (a: boolean) => a
        ? <Tag color="success">Active</Tag>
        : <Tag>Inactive</Tag> },
    { title: 'Source', key: 'source',
      render: (_: unknown, r: DirectionRule) => {
        if (r.admin_modified_at) return <Tag color="gold">Admin-modified</Tag>
        if (r.system_seeded) return <Tag color="blue">Factory v{r.factory_version}</Tag>
        return <Tag>User-created</Tag>
      } },
    { title: 'Actions', key: 'actions',
      render: (_: unknown, r: DirectionRule) => (
        <Space>
          <Tooltip title="Run now">
            <Button size="small" icon={<PlayCircleOutlined />}
                    onClick={() => runMut.mutate(r.id)} />
          </Tooltip>
          <Tooltip title="View / edit">
            <Button size="small" icon={<ToolOutlined />}
                    onClick={() => setSelected(r)} />
          </Tooltip>
          {r.active && (
            <Popconfirm title="Deactivate this rule?"
                        onConfirm={() => deactMut.mutate(r.id)}>
              <Button size="small" danger>Deactivate</Button>
            </Popconfirm>
          )}
          {r.admin_modified_at && (
            <Popconfirm title="Reset to factory defaults?"
                        onConfirm={() => resetMut.mutate(r.id)}>
              <Button size="small" icon={<RollbackOutlined />}>Reset</Button>
            </Popconfirm>
          )}
        </Space>
      ) },
  ]

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ThunderboltOutlined />} type="primary"
                onClick={() => runAllMut.mutate()}
                loading={runAllMut.isPending}>
          Run all active rules now
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => reseedMut.mutate()}
                loading={reseedMut.isPending}>
          Re-seed factory rules
        </Button>
      </Space>
      <Table
        rowKey="id"
        columns={columns as never}
        dataSource={data ?? []}
        pagination={{ pageSize: 20 }}
        size="small"
      />
      {selected && (
        <Modal
          open
          onCancel={() => setSelected(null)}
          onOk={() => setSelected(null)}
          title={selected.rule_name}
          width={720}
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Row gutter={[12, 8]}>
              <Col span={12}><Text type="secondary">Trigger</Text>
                <div>{selected.trigger_type}</div></Col>
              <Col span={12}><Text type="secondary">Frequency</Text>
                <div>{selected.frequency ?? '—'}</div></Col>
              <Col span={12}><Text type="secondary">Selection</Text>
                <div><code>{selected.selection_logic}</code></div></Col>
              <Col span={12}><Text type="secondary">Sample size</Text>
                <div>{selected.sample_size ?? '—'}</div></Col>
              <Col span={12}><Text type="secondary">Owner role</Text>
                <div>{selected.owner_role_code}</div></Col>
              <Col span={12}><Text type="secondary">Verifier role</Text>
                <div>{selected.verifier_role_code ?? '—'}</div></Col>
              <Col span={12}><Text type="secondary">Due</Text>
                <div>{selected.due_time_local ?? '—'}</div></Col>
              <Col span={12}><Text type="secondary">Priority</Text>
                <div><PriorityTag p={selected.priority} /></div></Col>
            </Row>
            {selected.threshold_config_json && (
              <>
                <Text type="secondary">Threshold config</Text>
                <pre style={{ fontSize: 12, background: '#fafafa', padding: 8 }}>
                  {JSON.stringify(selected.threshold_config_json, null, 2)}
                </pre>
              </>
            )}
            {selected.remarks && (
              <Alert type="info" message={selected.remarks} />
            )}
          </Space>
        </Modal>
      )}
    </>
  )
}

function GenerationLogsList() {
  const { data, isLoading } = useQuery({
    queryKey: ['study-direction-logs'],
    queryFn: () => studyDirectionApi.logs(undefined, 100),
  })
  if (isLoading) return <Skeleton active />
  const columns = [
    { title: 'Run time', dataIndex: 'run_time', key: 'run_time',
      render: (s: string) => new Date(s).toLocaleString() },
    { title: 'Rule', dataIndex: 'rule_code', key: 'rule_code',
      render: (c: string) => <code style={{ fontSize: 12 }}>{c}</code> },
    { title: 'Trigger', dataIndex: 'trigger_type', key: 'trigger_type' },
    { title: 'Source records', dataIndex: 'source_records_found', key: 'src' },
    { title: 'Created', dataIndex: 'tasks_created', key: 'created',
      render: (n: number) => <Tag color={n > 0 ? 'success' : 'default'}>{n}</Tag> },
    { title: 'Skipped (dup)', dataIndex: 'tasks_skipped_duplicate', key: 'skip' },
    { title: 'Status', dataIndex: 'status', key: 'status',
      render: (s: string) => (
        <Tag color={s === 'SUCCESS' ? 'success'
                    : s === 'FAILED' ? 'error' : 'default'}>{s}</Tag>
      ) },
    { title: 'Error', dataIndex: 'error_message', key: 'err',
      render: (e: string | null) => e ?? '' },
  ]
  return (
    <Table
      rowKey="id"
      columns={columns as never}
      dataSource={data ?? []}
      pagination={{ pageSize: 30 }}
      size="small"
    />
  )
}

export default function StudyDirectionsPage() {
  return (
    <div style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>Study Directions</Title>
      <Text type="secondary">
        Exactly which study to take, where, for whom, by when. 16 factory
        defaults ship — admin can edit, deactivate, duplicate, or reset.
      </Text>

      <Tabs
        defaultActiveKey="me"
        style={{ marginTop: 16 }}
        items={[
          {
            key: 'me',
            label: 'My Directed Studies',
            children: <Card><MyDirectedStudies /></Card>,
          },
          {
            key: 'admin',
            label: 'Rules (Admin)',
            children: <Card><DirectionRulesAdmin /></Card>,
          },
          {
            key: 'logs',
            label: 'Generation Logs',
            children: <Card><GenerationLogsList /></Card>,
          },
        ]}
      />
    </div>
  )
}
